import type { LocalizedDict } from '../index'

// Admin Settings → Integrations → Marketplace apps
// (features/admin/MarketplaceApps.tsx). Marketplace names, vendor console
// paths (docs hints) and vendor field names like "Keystring" stay as-is.

export type MarketplaceProviderId =
  | 'shopify'
  | 'etsy'
  | 'amazon'
  | 'printify'
  | 'jds'
  | 'hedi2'
  | 'gelato'
  | 'pinterest'

export type MarketplaceProviderText = {
  blurb: string
  clientIdLabel: string
  clientIdHint: string
  secretLabel: string
  lwaClientIdLabel?: string
  lwaClientIdHint?: string
  webhookSecretLabel?: string
  webhookSecretHint?: string
  docsHint?: string
}

export type MarketplaceAppsStrings = {
  providers: Record<MarketplaceProviderId, MarketplaceProviderText>
  sourceLabels: { db: string; env: string }
  title: string
  encryptedNote: string
  simulationNotice: string
  loading: string
  loadError: string
  savedToast: (label: string) => string
  savedToastDescription: string
  saveErrorToast: string
  unknownError: string
  credentialsLabel: (label: string) => string
  rotateSuffix: (secretLabel: string) => string
  secretHint: string
  liveMode: string
  active: string
  whereToFind: (docsHint: string) => string
  cancel: string
  saving: string
  rotateCredentials: string
  saveCredentials: string
  modeLive: string
  modeTest: string
}

export const marketplaceAppsI18n: LocalizedDict<MarketplaceAppsStrings> = {
  en: {
    providers: {
      shopify: {
        blurb: 'Connect merchants via OAuth to sync orders & listings.',
        clientIdLabel: 'API key (Client ID)',
        clientIdHint: 'Public — the app API key.',
        secretLabel: 'API secret key',
      },
      etsy: {
        blurb: 'Connect shops via OAuth to pull orders & listings.',
        clientIdLabel: 'Keystring (Client ID)',
        clientIdHint: 'Public — the app keystring.',
        secretLabel: 'Shared secret',
      },
      amazon: {
        blurb: 'Let sellers connect their Amazon store via SP-API (LWA OAuth).',
        clientIdLabel: 'Application ID',
        clientIdHint: 'Public — amzn1.sp.solution.…',
        lwaClientIdLabel: 'LWA Client ID',
        lwaClientIdHint: 'Public — amzn1.application-oa2-client.…',
        secretLabel: 'LWA Client Secret',
      },
      printify: {
        blurb: 'Supplier API — import the Printify catalog (blueprints, variants, shipping).',
        clientIdLabel: '',
        clientIdHint: '',
        secretLabel: 'Personal Access Token',
      },
      jds: {
        blurb: 'Supplier API — import product details for the curated JDS SKU list.',
        clientIdLabel: '',
        clientIdHint: '',
        secretLabel: 'API key',
      },
      hedi2: {
        blurb: 'Partner supplier API — import the Hedi2 catalog + receive product webhooks.',
        clientIdLabel: 'API base URL',
        clientIdHint: 'Public — the Hedi2 API root, e.g. https://api.hedi2.example',
        secretLabel: 'API key',
        webhookSecretLabel: 'Webhook HMAC secret',
        webhookSecretHint: 'Signs their product.upserted pushes. Encrypted at rest.',
        docsHint: 'Hedi2 × Pexify integration brief (key exchange)',
      },
      gelato: {
        blurb: 'Supplier API — import the Gelato print catalog (products, prices, stock).',
        clientIdLabel: '',
        clientIdHint: '',
        secretLabel: 'API key',
      },
      pinterest: {
        blurb: 'Pinterest Trends — trending Pinterest searches on every seller’s Trends screen.',
        clientIdLabel: 'App ID',
        clientIdHint: 'Public — the numeric Pinterest app id.',
        secretLabel: 'App secret key',
      },
    },
    sourceLabels: { db: 'saved credentials', env: 'server config' },
    title: 'Marketplace Apps',
    encryptedNote: 'OAuth app secrets are encrypted at rest (Fernet) and never displayed in full.',
    simulationNotice:
      'Simulation mode — marketplace credentials require the live backend (real-API build). Saving is disabled here.',
    loading: 'Loading…',
    loadError: 'Could not load marketplace credentials.',
    savedToast: (label) => `${label} credentials saved`,
    savedToastDescription: 'Secret encrypted at rest; only the last 4 chars are shown.',
    saveErrorToast: 'Could not save',
    unknownError: 'Unknown error',
    credentialsLabel: (label) => `${label} credentials`,
    rotateSuffix: (secretLabel) => `${secretLabel} (re-enter to rotate)`,
    secretHint: 'Encrypted at rest, never displayed again.',
    liveMode: 'Live mode',
    active: 'Active',
    whereToFind: (docsHint) => `Where do I find these? — ${docsHint}`,
    cancel: 'Cancel',
    saving: 'Saving…',
    rotateCredentials: 'Rotate credentials',
    saveCredentials: 'Save credentials',
    modeLive: 'LIVE',
    modeTest: 'TEST',
  },
  tr: {
    providers: {
      shopify: {
        blurb: 'Sipariş ve ilanları eşitlemek için mağazaları OAuth ile bağla.',
        clientIdLabel: 'API anahtarı (Client ID)',
        clientIdHint: 'Herkese açık — uygulamanın API anahtarı.',
        secretLabel: 'API gizli anahtarı',
      },
      etsy: {
        blurb: 'Sipariş ve ilanları çekmek için mağazaları OAuth ile bağla.',
        clientIdLabel: 'Keystring (Client ID)',
        clientIdHint: 'Herkese açık — uygulamanın keystring değeri.',
        secretLabel: 'Shared secret',
      },
      amazon: {
        blurb: 'Satıcılar Amazon mağazalarını SP-API (LWA OAuth) ile bağlasın.',
        clientIdLabel: 'Uygulama ID',
        clientIdHint: 'Herkese açık — amzn1.sp.solution.…',
        lwaClientIdLabel: 'LWA Client ID',
        lwaClientIdHint: 'Herkese açık — amzn1.application-oa2-client.…',
        secretLabel: 'LWA Client Secret',
      },
      printify: {
        blurb: 'Tedarikçi API’si — Printify kataloğunu içe aktar (şablonlar, varyantlar, kargo).',
        clientIdLabel: '',
        clientIdHint: '',
        secretLabel: 'Personal Access Token',
      },
      jds: {
        blurb: 'Tedarikçi API’si — seçili JDS SKU listesinin ürün bilgilerini içe aktar.',
        clientIdLabel: '',
        clientIdHint: '',
        secretLabel: 'API anahtarı',
      },
      hedi2: {
        blurb: 'Partner tedarikçi API’si — Hedi2 kataloğunu içe aktar + ürün webhook’larını al.',
        clientIdLabel: 'API temel URL',
        clientIdHint: 'Herkese açık — Hedi2 API kök adresi, örn. https://api.hedi2.example',
        secretLabel: 'API anahtarı',
        webhookSecretLabel: 'Webhook HMAC gizli anahtarı',
        webhookSecretHint: 'Gönderdikleri product.upserted bildirimlerini imzalar. Şifreli saklanır.',
        docsHint: 'Hedi2 × Pexify entegrasyon özeti (anahtar değişimi)',
      },
      gelato: {
        blurb: 'Tedarikçi API’si — Gelato baskı kataloğunu içe aktar (ürünler, fiyatlar, stok).',
        clientIdLabel: '',
        clientIdHint: '',
        secretLabel: 'API anahtarı',
      },
      pinterest: {
        blurb: 'Pinterest Trendleri — Pinterest’te yükselen aramalar, her satıcının Trendler ekranında.',
        clientIdLabel: 'App ID',
        clientIdHint: 'Herkese açık — Pinterest uygulamasının numarası.',
        secretLabel: 'App secret key',
      },
    },
    sourceLabels: { db: 'kayıtlı bilgiler', env: 'sunucu ayarı' },
    title: 'Pazar Yeri Uygulamaları',
    encryptedNote: 'OAuth uygulama gizli anahtarları şifreli saklanır (Fernet) ve hiçbir zaman tam gösterilmez.',
    simulationNotice:
      'Simülasyon modu — pazar yeri bilgileri için canlı sunucu gerekir (gerçek API sürümü). Burada kaydetme kapalı.',
    loading: 'Yükleniyor…',
    loadError: 'Pazar yeri bilgileri yüklenemedi.',
    savedToast: (label) => `${label} bilgileri kaydedildi`,
    savedToastDescription: 'Gizli anahtar şifreli saklanır; yalnızca son 4 karakteri gösterilir.',
    saveErrorToast: 'Kaydedilemedi',
    unknownError: 'Bilinmeyen hata',
    credentialsLabel: (label) => `${label} bilgileri`,
    rotateSuffix: (secretLabel) => `${secretLabel} (değiştirmek için yeniden girin)`,
    secretHint: 'Şifreli saklanır, bir daha gösterilmez.',
    liveMode: 'Canlı mod',
    active: 'Aktif',
    whereToFind: (docsHint) => `Bunları nerede bulurum? — ${docsHint}`,
    cancel: 'Vazgeç',
    saving: 'Kaydediliyor…',
    rotateCredentials: 'Bilgileri değiştir',
    saveCredentials: 'Bilgileri kaydet',
    modeLive: 'CANLI',
    modeTest: 'TEST',
  },
}
